import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Grid,
  Paper,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import rootAxios from 'requests/rootAxios';
import { toast } from 'react-toastify';

const EmailChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
  },
}));

export default function AdditionalDetailsForm({ onBack, onDataChange, batchId }) {
  const [emailData, setEmailData] = useState({
    to: [],
    cc: [],
  });
  const [currentToEmail, setCurrentToEmail] = useState('');
  const [currentCCEmail, setCurrentCCEmail] = useState('');
  const [thankYouNotes, setThankYouNotes] = useState([{ name: '', designation: '' }]);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingEmails, setLoadingEmails] = useState(false);

  // Fetch feedback emails from API
  const fetchFeedbackEmails = async () => {
    if (!batchId) return;

    setLoadingEmails(true);
    try {
      const response = await rootAxios.post('/user/get-feedbackemails', {
        batchId: batchId,
      });
      console.log('response', response);
      if (response?.data?.data?.success) {
        const { to, cc } = response.data.data.data;
        // console.log('Raw API response:', response.data);
        // console.log('Extracted to:', to, 'Type:', typeof to);
        // console.log('Extracted cc:', cc, 'Type:', typeof cc);

        const newEmailData = {
          to: Array.isArray(to) ? to : to ? [to] : [],
          cc: Array.isArray(cc) ? cc : cc ? [cc] : [],
        };

        console.log('Setting email data to:', newEmailData);
        setEmailData(newEmailData);

        // Show success message if emails were loaded
        if (newEmailData.to.length > 0 || newEmailData.cc.length > 0) {
          toast.success(
            `Feedback emails loaded: ${newEmailData.to.length} To, ${newEmailData.cc.length} CC`
          );
        }
      } else {
        console.log('API response not successful or no data:', response?.data);
      }
    } catch (error) {
      console.error('Error fetching feedback emails:', error);
      toast.error('Failed to fetch feedback emails');
    } finally {
      setLoadingEmails(false);
    }
  };

  // Fetch emails when component mounts or batchId changes
  useEffect(() => {
    console.log('AdditionalDetailsForm useEffect triggered, batchId:', batchId);
    fetchFeedbackEmails();
  }, [batchId]);

  // Debug component mount
  useEffect(() => {
    console.log('AdditionalDetailsForm mounted with batchId:', batchId);
  }, []);

  const handleThankYouNoteChange = (index, field, value) => {
    const newNotes = [...thankYouNotes];
    newNotes[index][field] = value;
    setThankYouNotes(newNotes);
  };

  const addThankYouNote = () => {
    setThankYouNotes([...thankYouNotes, { name: '', designation: '' }]);
  };

  const removeThankYouNote = (index) => {
    const newNotes = thankYouNotes.filter((_, i) => i !== index);
    setThankYouNotes(newNotes);
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    if (photos.length + files.length > 6) {
      setErrors((prev) => ({
        ...prev,
        photos: 'Maximum 6 photos allowed',
      }));
      return;
    }
    setPhotos([...photos, ...files]);
    setErrors((prev) => ({ ...prev, photos: '' }));
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
  };

  const handleEmailKeyDown = (field, e, currentEmail, setCurrentEmail) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const email = currentEmail.trim();
      if (email) {
        const emails = email
          .split(',')
          .map((e) => e.trim())
          .filter((e) => e && isValidEmail(e));
        if (emails.length > 0) {
          setEmailData((prev) => ({
            ...prev,
            [field]: [...prev[field], ...emails],
          }));
          setCurrentEmail('');
        }
      }
    }
  };

  const handlePaste = (field, e, setCurrentEmail) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e && isValidEmail(e));

    if (emails.length > 0) {
      setEmailData((prev) => ({
        ...prev,
        [field]: [...prev[field], ...emails],
      }));
      setCurrentEmail('');
    }
  };

  const handleDeleteEmail = (field, emailToDelete) => {
    setEmailData((prev) => ({
      ...prev,
      [field]: prev[field].filter((email) => email !== emailToDelete),
    }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (emailData.to.length === 0) {
      newErrors.emailRecipients = 'At least one To email is required';
    }

    if (thankYouNotes.some((note) => !note.name.trim() || !note.designation.trim())) {
      newErrors.thankYouNotes = 'All thank you note fields are required';
    }

    if (photos.length < 4) {
      newErrors.photos = 'Minimum 4 photos required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Debug emailData changes
  React.useEffect(() => {
    console.log('emailData changed:', emailData);
  }, [emailData]);

  // Notify parent component when data changes
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange({
        emailRecipients: emailData,
        thankYouNotes,
        photos,
      });
    }
  }, [emailData, thankYouNotes, photos, onDataChange]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Additional Training Details
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
            To Emails
          </Typography>
          {loadingEmails && <CircularProgress size={16} />}
          <Button
            size="small"
            variant="outlined"
            onClick={fetchFeedbackEmails}
            disabled={loadingEmails || !batchId}
            sx={{ ml: 'auto' }}
          >
            Refresh
          </Button>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {emailData.to.length > 0 ? (
            emailData.to.map((email, index) => (
              <EmailChip
                key={index}
                label={email}
                onDelete={() => handleDeleteEmail('to', email)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No emails loaded
            </Typography>
          )}
        </Stack>
        <TextField
          fullWidth
          label="Add To Email"
          value={currentToEmail}
          onChange={(e) => setCurrentToEmail(e.target.value)}
          onKeyDown={(e) => handleEmailKeyDown('to', e, currentToEmail, setCurrentToEmail)}
          onPaste={(e) => handlePaste('to', e, setCurrentToEmail)}
          margin="normal"
          type="email"
          error={!!errors.emailRecipients}
          helperText={
            errors.emailRecipients || 'Press space, enter, or paste comma-separated emails'
          }
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
            CC Emails
          </Typography>
          {loadingEmails && <CircularProgress size={16} />}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {emailData.cc.length > 0 ? (
            emailData.cc.map((email, index) => (
              <EmailChip
                key={index}
                label={email}
                onDelete={() => handleDeleteEmail('cc', email)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No emails loaded
            </Typography>
          )}
        </Stack>
        <TextField
          fullWidth
          label="Add CC Email"
          value={currentCCEmail}
          onChange={(e) => setCurrentCCEmail(e.target.value)}
          onKeyDown={(e) => handleEmailKeyDown('cc', e, currentCCEmail, setCurrentCCEmail)}
          onPaste={(e) => handlePaste('cc', e, setCurrentCCEmail)}
          margin="normal"
          type="email"
          helperText="Press space, enter, or paste comma-separated emails"
        />
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        Name and Designation of the people we want to thank
      </Typography>
      {thankYouNotes.map((note, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={5}>
            <TextField
              fullWidth
              label="Name"
              value={note.name}
              onChange={(e) => handleThankYouNoteChange(index, 'name', e.target.value)}
              error={!!errors.thankYouNotes}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              fullWidth
              label="Designation"
              value={note.designation}
              onChange={(e) => handleThankYouNoteChange(index, 'designation', e.target.value)}
              error={!!errors.thankYouNotes}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              onClick={() => removeThankYouNote(index)}
              disabled={thankYouNotes.length === 1}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button variant="outlined" onClick={addThankYouNote} sx={{ mb: 3 }}>
        Add Another Person
      </Button>

      <Typography variant="subtitle1" gutterBottom>
        Training Photographs (4-6 photos required)
      </Typography>
      <Box sx={{ mb: 3 }}>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="photo-upload"
          type="file"
          multiple
          onChange={handlePhotoUpload}
        />
        <label htmlFor="photo-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            disabled={photos.length >= 6}
          >
            Upload Photos
          </Button>
        </label>
        {errors.photos && (
          <Typography color="error" variant="caption" display="block">
            {errors.photos}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {photos.map((photo, index) => (
          <Grid item xs={4} key={index}>
            <Paper
              sx={{
                p: 1,
                position: 'relative',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={URL.createObjectURL(photo)}
                alt={`Training photo ${index + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
                onClick={() => removePhoto(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
    </Box>
  );
}
